import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isBookingInProgress,
  isDiwaliCampaignActive,
  markDiwaliPopupSeen,
  prefersReducedMotion,
  wasDiwaliPopupSeen,
} from '../data/diwaliCampaign';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Survives a dev remount so the greeting stays open until the visitor closes it. */
let diwaliPopupHeld = false;

const Diya = ({ className = '' }) => (
  <svg className={`diwali-diya ${className}`.trim()} viewBox="0 0 80 64" aria-hidden="true">
    <path d="M40 4c5 9 10 14 10 22a10 10 0 1 1-20 0c0-8 5-13 10-22z" fill="#FFE08A" />
    <path d="M40 16c2.4 4.5 4 7 4 11a4 4 0 1 1-8 0c0-4 1.6-6.5 4-11z" fill="#FF9A1F" />
    <path d="M12 40c8 12 16 16 28 16s20-4 28-16c-10 5-46 5-56 0z" fill="#F5B800" />
    <path d="M16 40h48c-2 4-10 6-24 6s-22-2-24-6z" fill="#d9a000" />
    <rect x="34" y="54" width="12" height="5" rx="1.5" fill="#F5B800" />
  </svg>
);

const DiwaliPopup = () => {
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocus = useRef(null);
  const titleId = useId();
  const bodyId = useId();
  const [open, setOpen] = useState(false);
  const [motionOk, setMotionOk] = useState(true);

  useEffect(() => {
    if (diwaliPopupHeld) {
      setMotionOk(!prefersReducedMotion());
      setOpen(true);
      return undefined;
    }
    if (!isDiwaliCampaignActive() || wasDiwaliPopupSeen()) return undefined;
    const timer = window.setTimeout(() => {
      if (wasDiwaliPopupSeen() || isBookingInProgress()) return;
      previousFocus.current = document.activeElement;
      diwaliPopupHeld = true;
      markDiwaliPopupSeen();
      setMotionOk(!prefersReducedMotion());
      setOpen(true);
    }, 400);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previousBody = document.body.style.overflow;
    const previousRoot = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)].filter((node) => !node.hasAttribute('disabled'));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousRoot;
      document.removeEventListener('keydown', onKeyDown);
      const previous = previousFocus.current;
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }, [open]);

  const close = () => {
    diwaliPopupHeld = false;
    markDiwaliPopupSeen();
    setOpen(false);
  };

  const bookRide = () => {
    diwaliPopupHeld = false;
    markDiwaliPopupSeen();
    setOpen(false);
    if (window.location.pathname === '/') {
      window.history.replaceState(null, '', '/#home-booking');
      window.setTimeout(() => {
        document.getElementById('home-booking')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 40);
      return;
    }
    navigate('/#home-booking');
  };

  const exploreRoutes = () => {
    diwaliPopupHeld = false;
    markDiwaliPopupSeen();
    setOpen(false);
    navigate('/taxi-routes');
  };

  if (!open) return null;

  return (
    <div
      className="diwali-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        className="diwali-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
      >
        {motionOk ? (
          <div className="diwali-sparks" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        ) : null}
        <button ref={closeRef} type="button" className="diwali-close" onClick={close} aria-label="Close Diwali greeting">
          <span aria-hidden="true">&times;</span>
        </button>
        <img className="diwali-logo" src="/images/logo-light.png" alt="Easy Drop Taxi" width="168" height="52" />
        <p className="diwali-title" id={titleId}>Happy Diwali</p>
        <p className="diwali-from">from EasyDropTaxi!</p>
        <p className="diwali-message" id={bodyId}>
          May this Festival of Lights bring happiness, prosperity, and safe journeys to you and your loved ones.
        </p>
        <div className="diwali-actions">
          <button type="button" className="diwali-btn diwali-btn--gold" onClick={bookRide}>
            Book Your Diwali Ride
          </button>
          <button type="button" className="diwali-btn diwali-btn--ghost" onClick={exploreRoutes}>
            Explore Taxi Routes
          </button>
        </div>
        <Diya />
        <Diya className="diwali-diya--right" />
      </div>
    </div>
  );
};

export default DiwaliPopup;
